import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AppContext } from "../../common/AppContext";
import { Utils } from "../../common/Utils";
import { Button } from "../../components/Button";
import { DetailsGrid } from "../../components/DetailsGrid";
import { DetailsGridRow } from "../../components/DetailsGridRow";
import { Paper } from "../../components/Paper";
import { PaymentMethodCard } from "../../components/PaymentMethodCard";
import { BillingHistoryTable } from "../../components/table/BillingHistoryTable";
import { TablePlaceholder } from "../../components/TablePlaceholder";
import { ViewComponent } from "../../components/ViewComponent";
import { BillingPlan } from "../../data/billing/BillingPlan";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import CreditCardIcon from "../../resources/images/icons-credit-card.svg";
import BillingIcon from "../../resources/images/icons-invoice.svg";

interface SettingsBillingViewProps extends WithLocaleProps {
  theme?: any;
  billingDetails?: any;
  billingInvoices?: any;
  history?: any;
  location?: any;
  onLoadBillingDetails?: () => void;
  onShowBillingPlans?: () => void;
  onShowBillingDetails?: () => void;
  onLoadBillingInvoices?: () => void;
  onShowCreditCardDetails?: () => void;
  onInvoiceDownload?: (e: any, invoice: any) => void;
}

class SettingsBillingViewBase extends ViewComponent<SettingsBillingViewProps> {
  static contextType = AppContext;

  componentDidMount() {
    const {
      onLoadBillingDetails,
      onShowBillingPlans,
      onShowBillingDetails,
      onLoadBillingInvoices,
      onShowCreditCardDetails
    } = this.props;
    onLoadBillingDetails?.();

    if (this.pathStartsWith("/settings/billing/plan")) onShowBillingPlans?.();
    else if (this.pathStartsWith("/settings/billing/history") && onLoadBillingInvoices)
      onLoadBillingInvoices?.();
    else if (this.pathStartsWith("/settings/billing/details")) onShowBillingDetails?.();
    else if (this.pathStartsWith("/settings/billing/payment/update-card"))
      onShowCreditCardDetails?.();
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    super.componentDidUpdate(prevProps, prevState);
    if (this.props.billingDetails !== prevProps.billingDetails) this.forceUpdate();
  }

  onViewChange(location: any) {
    const {
      onShowBillingPlans,
      onShowBillingDetails,
      onLoadBillingInvoices,
      onShowCreditCardDetails
    } = this.props;

    if (this.pathStartsWith("/settings/billing/plan") && onShowBillingPlans) onShowBillingPlans();
    else if (this.pathStartsWith("/settings/billing/history") && onLoadBillingInvoices)
      onLoadBillingInvoices();
    else if (this.pathStartsWith("/settings/billing/details")) onShowBillingDetails?.();
    else if (this.pathStartsWith("/settings/billing/payment/update-card"))
      onShowCreditCardDetails?.();
  }

  onTabChange(e: any, step: any) {
    const { history } = this.props;
    if (step == 0) history.push("/settings/billing");
    else if (step == 1) history.push("/settings/billing/history");
    else if (step == 2) history.push("/settings/billing/payment");
  }

  onInvoiceDownload(e: any, invoice: any) {
    const { onInvoiceDownload } = this.props;
    if (onInvoiceDownload) onInvoiceDownload(e, invoice);
  }

  onChangeCardClick(e: any) {
    const { history } = this.props;
    history.push("/settings/billing/payment/update-card");
  }

  onContactUsLinkClick(e: any) {
    //e.preventDefault();
    //const { onContactUs } = this.props;
    //if (onContactUs) onContactUs(e);
  }

  render() {
    const { theme, billingDetails, billingInvoices, translate } = this.props;
    const contactDetails = billingDetails ? billingDetails.contactDetails : null;
    const creditCardDetails = billingDetails ? billingDetails.creditCardDetails : null;

    const isInvoice =
      billingDetails && billingDetails.paymentMethod === BillingPlan.paymentMethod.invoice;

    let step = 0;
    if (this.pathStartsWith("/settings/billing/history")) step = 1;
    else if (this.pathStartsWith("/settings/billing/payment")) step = 2;

    return (
      <div>
        <Paper theme={theme} padding={{ xs: "46px 24px", md: "60px" }} borderFromBreakpoint='md'>
          <h1>{translate("settingsViews.billing.title")}</h1>
          <Stack direction='column' spacing={4}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                centered={true}
                value={step}
                onChange={this.onTabChange.bind(this)}
                variant='fullWidth'
                sx={{ width: "100%" }}
              >
                <Tab label={translate("billing.overview")} value={0} />
                <Tab label={translate("billing.billingHistory")} value={1} />
                <Tab label={translate("billing.paymentMethod")} value={2} />
              </Tabs>
            </Box>

            {step === 0 && (
              <div>
                <Stack direction='column' spacing={4}>
                  <div>
                    <h2>{translate("billing.yourCurrentPlan")}</h2>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent='space-between'
                      spacing={2}
                    >
                      <div
                        className='klayo-text-md'
                        dangerouslySetInnerHTML={{
                          __html: translate("billing.youAreCurrentlyOnPlan", {
                            planName: billingDetails
                              ? billingDetails.planGroupName || billingDetails.planName
                              : ""
                          })
                        }}
                      />
                      <div>
                        <Button
                          path={"/settings/billing/plan"}
                          size='md'
                          disabled={billingDetails && billingDetails.isCustomPlan}
                          label={translate("billing.changePlan")}
                        />
                      </div>
                    </Stack>
                  </div>

                  {billingDetails && (
                    <div style={{ borderTop: "1px solid #0000001f", padding: "24px 0" }}>
                      <h2>{translate("billing.planDetails")}</h2>
                      <DetailsGrid spacing={2}>
                        <DetailsGridRow label={translate("billing.monthlyCost")}>
                          {!isNaN(billingDetails.planPricePerCycleInCents)
                            ? "$" +
                              Utils.formatCurrency(billingDetails.planPricePerCycleInCents / 100)
                            : "-"}
                        </DetailsGridRow>
                        <DetailsGridRow label={translate("billing.users")}>
                          {Utils.formatNumber(billingDetails.planEmployeeCap)} (
                          {billingDetails.currentNumberOfEmployeesInOrg} {translate("billing.used")}
                          )
                        </DetailsGridRow>
                        <DetailsGridRow label={translate("billing.nextBill")}>
                          {billingDetails.currentCycleEndDate
                            ? billingDetails.currentCycleEndDate.toDateString()
                            : "-"}
                        </DetailsGridRow>
                        <DetailsGridRow label={translate("billing.paymentMethod")}>
                          {billingDetails.paymentMethod
                            ? billingDetails.paymentMethod.label
                            : translate("billing.none")}
                        </DetailsGridRow>
                      </DetailsGrid>
                    </div>
                  )}

                  {contactDetails && (
                    <div style={{ borderTop: "1px solid #0000001f", padding: "24px 0" }}>
                      <h2>{translate("billing.billingDetails")}</h2>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent='space-between'
                        spacing={2}
                      >
                        <DetailsGrid spacing={2}>
                          <DetailsGridRow
                            label={translate("billing.billingContact")}
                            lineHeight='24px'
                            direction={{ xs: "column", md: "row" }}
                          >
                            <div>
                              {contactDetails.contactFirstName} {contactDetails.contactLastName}
                            </div>
                            <div>{contactDetails.contactEmail}</div>
                            <div>{contactDetails.contactPhone}</div>
                          </DetailsGridRow>
                          <DetailsGridRow
                            label={translate("billing.billingDetails")}
                            lineHeight='24px'
                            direction={{ xs: "column", md: "row" }}
                          >
                            <div>{contactDetails.companyName}</div>
                            <div>{contactDetails.street1}</div>
                            <div>
                              {contactDetails.city}, {contactDetails.state},{" "}
                              {contactDetails.postalCode}
                            </div>
                            <div>{contactDetails.country}</div>
                          </DetailsGridRow>
                        </DetailsGrid>
                        <div>
                          <Button
                            path={"/settings/billing/details"}
                            size='md'
                            disabled={billingDetails && billingDetails.isCustomPlan}
                            label={translate("billing.changeDetails")}
                          />
                        </div>
                      </Stack>
                    </div>
                  )}
                </Stack>
              </div>
            )}

            {step === 1 && (
              <div>
                <BillingHistoryTable
                  paper={false}
                  theme={theme}
                  minHeight='300px'
                  showHeader={true}
                  showFooter={false}
                  selectable={false}
                  rows={billingInvoices ? billingInvoices.invoices : null}
                  onClickDownloadLink={this.onInvoiceDownload.bind(this)}
                  translate={translate}
                  columns={[
                    {
                      type: BillingHistoryTable.columns.date,
                      label: translate("billing.date")
                    },
                    {
                      type: BillingHistoryTable.columns.invoice,
                      label: translate("billing.invoice")
                    },
                    {
                      type: BillingHistoryTable.columns.billedTo,
                      label: translate("billing.billedTo")
                    },
                    {
                      type: BillingHistoryTable.columns.amount,
                      label: translate("billing.amount")
                    },
                    {
                      type: BillingHistoryTable.columns.paid,
                      label: translate("billing.paid")
                    },
                    {
                      type: BillingHistoryTable.columns.status,
                      label: translate("tableColumns.status")
                    }
                  ]}
                  noRowsComponent={
                    <TablePlaceholder
                      text={translate("billing.billingHistoryPlaceholder")}
                      icon={<BillingIcon />}
                    />
                  }
                />
              </div>
            )}

            {step === 2 && (
              <div>
                {billingDetails &&
                (isInvoice ||
                  (billingDetails.paymentMethod === BillingPlan.paymentMethod.creditCard &&
                    creditCardDetails)) ? (
                  <Stack direction='column' spacing={4} sx={{ width: "100%" }}>
                    <div>
                      <PaymentMethodCard
                        billingDetails={billingDetails}
                        creditCardDetails={creditCardDetails}
                        onChangeCardClick={this.onChangeCardClick.bind(this)}
                        translate={translate}
                      />
                    </div>
                    <div style={{ fontSize: "16px" }}>
                      {translate("billing.changePaymentMethodQuestion", {
                        method: isInvoice
                          ? translate("billing.creditCard")
                          : translate("billing.invoice").toLowerCase()
                      })}{" "}
                      <Link
                        href='https://www.klayo.com/contact-us/'
                        target='_blank'
                        onClick={this.onContactUsLinkClick.bind(this)}
                        style={{
                          textDecoration: "none",
                          fontWeight: "600",
                          color: "var(--neon-blue-nb-500)"
                        }}
                      >
                        {translate("billing.contactUs")}
                      </Link>
                    </div>
                  </Stack>
                ) : (
                  <TablePlaceholder
                    text={translate("billing.noBillingMethodAdded")}
                    icon={<CreditCardIcon />}
                    sx={{ position: "static", height: "90px" }}
                  />
                )}
              </div>
            )}
          </Stack>
        </Paper>

        {this.renderSnackbar()}
      </div>
    );
  }
}

export const SettingsBillingView = withLocale(SettingsBillingViewBase);
